<template>
  <div class="readiness-configuration" data-testid="readiness-configuration">
    <!-- Toolbar: back to the Configuration panel grid. -->
    <div class="readiness-configuration__toolbar">
      <button
        type="button"
        class="readiness-configuration__back"
        aria-label="Back to configuration"
        data-testid="readiness-back"
        @click="emit('go-back')"
      >
        <img :src="backIcon" alt="" aria-hidden="true" />
      </button>
    </div>

    <div class="readiness-configuration__section">
      <!-- Section header. -->
      <div class="readiness-configuration__header">
        <h2 class="readiness-configuration__title">Readiness</h2>
        <p class="readiness-configuration__subtitle">
          This configuration controls management of badges
        </p>
      </div>

      <hr class="readiness-configuration__divider" />

      <p
        v-if="errorMessage"
        class="readiness-configuration__error"
        role="alert"
        data-testid="readiness-error"
      >
        {{ errorMessage }}
      </p>

      <!-- Given Badges row. -->
      <div class="readiness-configuration__row">
        <div class="readiness-configuration__label-block">
          <span class="readiness-configuration__name">Given Badges</span>
          <span class="readiness-configuration__description">
            Badges that are Given instead of being Awarded using a checklist
          </span>
        </div>
        <div class="readiness-configuration__toggle-block">
          <SwitchButton
            v-model="removeGivenBadgesOn"
            :label="'Remove given badges once a day automatically when obsolete'"
            :toggleLoader="saving"
            :disabled="loading"
            data-testid="readiness-toggle"
            @onChange="handleChange"
          />
          <span class="readiness-configuration__supporting-text">
            When a badge is given and later the checkpoint activates at least one checkpoint then
            automatically remove the given badges once a day
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import SwitchButton from "@components/SwitchButton/SwitchButton.vue";
import { getAccountData, updateDirectory } from "@/api";

const props = defineProps({
  /**
   * Account id to load readiness configuration for. Falls back to the
   * `currentAccountID` localStorage value used by the rest of the Settings flow.
   */
  accountId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(["go-back"]);

const backIcon = require("@components/assets/side-bar/backIcon.svg");

const removeGivenBadgesOn = ref(false);
const loading = ref(false);
const saving = ref(false);
const errorMessage = ref("");

const resolvedAccountId = computed(() => {
  if (props.accountId !== null && props.accountId !== undefined && props.accountId !== "") {
    return props.accountId;
  }
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return null;
  }
});

async function fetchReadinessConfiguration() {
  const accountId = resolvedAccountId.value;
  if (accountId === null || accountId === undefined) {
    errorMessage.value = "Could not determine the account to load readiness configuration for.";
    return;
  }

  loading.value = true;
  errorMessage.value = "";
  try {
    const { data } = await getAccountData(accountId);
    removeGivenBadgesOn.value = data?.automatically_remove_given_badges === true;
  } catch {
    removeGivenBadgesOn.value = false;
    errorMessage.value = "Failed to load readiness configuration. Please try again.";
  } finally {
    loading.value = false;
  }
}

async function persistToggle(value) {
  const accountId = resolvedAccountId.value;
  if (accountId === null || accountId === undefined) {
    errorMessage.value = "Could not determine the account to update.";
    return false;
  }

  errorMessage.value = "";
  try {
    await updateDirectory(accountId, { automatically_remove_given_badges: value });
    return true;
  } catch {
    errorMessage.value = "Failed to save this setting. Please try again.";
    return false;
  }
}

async function handleChange({ value }) {
  if (saving.value) return;
  saving.value = true;
  try {
    const saved = await persistToggle(value);
    if (!saved) {
      removeGivenBadgesOn.value = !value;
    }
  } finally {
    saving.value = false;
  }
}

onMounted(fetchReadinessConfiguration);
</script>

<style scoped>
.readiness-configuration {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.readiness-configuration__toolbar {
  display: flex;
  align-items: center;
}

.readiness-configuration__back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
}

.readiness-configuration__back:hover {
  background: #f9fafb;
}

.readiness-configuration__back:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.readiness-configuration__section {
  width: 100%;
  max-width: 1032px;
  padding: 32px;
}

.readiness-configuration__header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 20px;
}

.readiness-configuration__title {
  margin: 0;
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.readiness-configuration__subtitle {
  margin: 0;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.readiness-configuration__error {
  margin: 16px 0;
  padding: 12px 16px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #b42318;
  font-size: 14px;
  line-height: 20px;
}

.readiness-configuration__row {
  display: flex;
  align-items: flex-start;
  gap: 64px;
  padding: 16px 0;
}

.readiness-configuration__label-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 0 0 280px;
  max-width: 280px;
}

.readiness-configuration__name {
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.readiness-configuration__description {
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.readiness-configuration__toggle-block {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
}

.readiness-configuration__supporting-text {
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

.readiness-configuration__divider {
  border: 0;
  border-top: 1px solid #eaecf0;
  margin: 0;
  width: 100%;
}
</style>
